export type InAppBrowserSource =
  | 'instagram'
  | 'facebook'
  | 'tiktok'
  | 'line'
  | 'snapchat'
  | 'gsa'
  | 'gmail'
  | 'generic';

export interface InAppBrowserInfo {
  isInAppBrowser: boolean;
  source: InAppBrowserSource | null;
}

let cached: InAppBrowserInfo | null = null;

export function detectInAppBrowser(): InAppBrowserInfo {
  if (cached) return cached;

  const ua = navigator.userAgent || '';

  // PWA standalone mode is NOT an in-app browser
  if (window.matchMedia?.('(display-mode: standalone)').matches) {
    cached = { isInAppBrowser: false, source: null };
    return cached;
  }

  // Specific app detection (ordered by prevalence). Google-family apps need
  // explicit matchers because their UAs keep the "Safari/" token that would
  // otherwise pass the generic iOS check. Google auth flows break inside
  // these WebViews because third-party popups are blocked, so detecting
  // them means we fall back to the magic-link path instead of offering
  // OAuth buttons that silently return { token: undefined }.
  if (/Instagram/i.test(ua)) {
    cached = { isInAppBrowser: true, source: 'instagram' };
  } else if (/FBAN|FBAV/i.test(ua)) {
    cached = { isInAppBrowser: true, source: 'facebook' };
  } else if (/BytedanceWebview|TikTok|musical_ly/i.test(ua)) {
    cached = { isInAppBrowser: true, source: 'tiktok' };
  } else if (/\bLine\//i.test(ua)) {
    cached = { isInAppBrowser: true, source: 'line' };
  } else if (/Snapchat/i.test(ua)) {
    cached = { isInAppBrowser: true, source: 'snapchat' };
  } else if (/\bGSA\//i.test(ua)) {
    cached = { isInAppBrowser: true, source: 'gsa' };
  } else if (/\bGmailiOS\b|\bGoogleMobile\//i.test(ua)) {
    cached = { isInAppBrowser: true, source: 'gmail' };
  } else if (isGenericWebView(ua)) {
    cached = { isInAppBrowser: true, source: 'generic' };
  } else {
    cached = { isInAppBrowser: false, source: null };
  }

  return cached;
}

function isGenericWebView(ua: string): boolean {
  // Android WebView: contains "; wv)" in the UA
  if (/; wv\)/i.test(ua)) return true;

  // iOS WebView: has AppleWebKit but NOT the Safari/ token
  // Normal Safari/Chrome iOS always include "Safari/" at the end
  if (/AppleWebKit/i.test(ua) && !/Safari\//i.test(ua)) return true;

  return false;
}

/** Reset cache (for testing) */
export function _resetInAppBrowserCache(): void {
  cached = null;
}
